/** Browser-local saved invoice letterhead / GST defaults (per workstation). */

import type { InvoiceType } from "../api/inventoryInvoicesApi";
import { normalizeLetterheadLogoUrl } from "./constructionPoDocument";

export const INVOICE_FORMAT_STORAGE_KEY = "indiramma.invoiceFormat.v1";

export type InvoiceFormatGstRowSaved = {
  label: string;
  ratePercent: number;
};

export type SubcontractorBillSource = "milestone" | "timeline";

export type SubcontractorBillLineSetting = {
  billSource: SubcontractorBillSource;
  milestoneTemplateId: string;
  milestoneTemplateItemId: string;
  milestoneTitle: string;
  lineLabel: string;
  timelinePhaseIds: string[];
  timelinePhaseTitles: string[];
  qty: number;
  unit: string;
  rate: number;
  hsnCode: string;
};

/** Bill From/To, GST, and footer settings for one invoice type. */
export type InvoiceTypeFormatBlock = {
  billFromName: string;
  billFromAddress: string;
  billFromGstin: string;
  billToName: string;
  billToAddress: string;
  billToGstin: string;
  paymentTerms: string;
  footerNote: string;
  aggregateQtyDefault: boolean;
  gstRows: InvoiceFormatGstRowSaved[];
};

export type InvoiceFormatSettings = {
  version: 2;
  documentTitleMaterial: string;
  documentTitleSubcontractor: string;
  documentTitleLabour: string;
  letterheadTagline: string;
  logoPrimaryUrl: string;
  logoSecondaryUrl: string;
  logoPrimaryHeightPx: number;
  logoSecondaryHeightPx: number;
  accentColorHex: string;
  material: InvoiceTypeFormatBlock;
  subcontractor: InvoiceTypeFormatBlock;
  labour: InvoiceTypeFormatBlock;
  subcontractorBillLines: SubcontractorBillLineSetting[];
  labourBillLines: SubcontractorBillLineSetting[];
};

function clampLogoHeight(n: number): number {
  if (!Number.isFinite(n)) return 56;
  return Math.min(160, Math.max(28, Math.round(n)));
}

function sanitizeAccentHex(hex: string): string {
  const t = hex.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(t)) return t;
  return "#16365f";
}

function defaultTypeFormatBlock(): InvoiceTypeFormatBlock {
  return {
    billFromName: "",
    billFromAddress: "",
    billFromGstin: "",
    billToName: "",
    billToAddress: "",
    billToGstin: "",
    paymentTerms: "As per agreement. Retention and statutory deductions as applicable.",
    footerNote: "",
    aggregateQtyDefault: true,
    gstRows: [
      { label: "CGST", ratePercent: 9 },
      { label: "SGST", ratePercent: 9 },
    ],
  };
}

export function defaultInvoiceFormatSettings(): InvoiceFormatSettings {
  const block = defaultTypeFormatBlock();
  return {
    version: 2,
    documentTitleMaterial: "MATERIAL TAX INVOICE",
    documentTitleSubcontractor: "SUBCONTRACTOR RA BILL",
    documentTitleLabour: "LABOUR SUPPLIER INVOICE",
    letterheadTagline: "",
    logoPrimaryUrl: "",
    logoSecondaryUrl: "",
    logoPrimaryHeightPx: 56,
    logoSecondaryHeightPx: 68,
    accentColorHex: "#16365f",
    material: { ...block, gstRows: block.gstRows.map((r) => ({ ...r })) },
    subcontractor: { ...block, gstRows: block.gstRows.map((r) => ({ ...r })) },
    labour: { ...block, gstRows: block.gstRows.map((r) => ({ ...r })) },
    subcontractorBillLines: [],
    labourBillLines: [],
  };
}

function clampGstRows(rows: unknown): InvoiceFormatGstRowSaved[] {
  const fallback = defaultTypeFormatBlock().gstRows;
  // Missing key → factory defaults; empty array [] → user chose no GST rows.
  if (rows === undefined || rows === null) return fallback.map((r) => ({ ...r }));
  if (!Array.isArray(rows)) return fallback.map((r) => ({ ...r }));
  const out: InvoiceFormatGstRowSaved[] = [];
  for (const r of rows) {
    if (!r || typeof r !== "object") continue;
    const o = r as { label?: unknown; ratePercent?: unknown };
    const label = typeof o.label === "string" ? o.label.trim().slice(0, 64) : "Tax";
    const rp = Number(o.ratePercent);
    out.push({
      label: label || "Tax",
      ratePercent: Number.isFinite(rp) ? Math.min(100, Math.max(0, rp)) : 0,
    });
  }
  return out;
}

/** GST rows with a positive rate — used for PDF totals and invoice generation. */
export function activeGstRows(s: InvoiceFormatSettings, type: InvoiceType): InvoiceFormatGstRowSaved[] {
  return formatBlockForInvoiceType(s, type).gstRows.filter((g) => g.ratePercent > 0.0000001);
}

function clampTypeFormatBlock(raw: unknown, fallback?: InvoiceTypeFormatBlock): InvoiceTypeFormatBlock {
  const d = fallback ?? defaultTypeFormatBlock();
  if (!raw || typeof raw !== "object") return { ...d, gstRows: d.gstRows.map((r) => ({ ...r })) };
  const o = raw as Record<string, unknown>;
  return {
    billFromName: typeof o.billFromName === "string" ? o.billFromName.slice(0, 2000) : d.billFromName,
    billFromAddress: typeof o.billFromAddress === "string" ? o.billFromAddress.slice(0, 4000) : d.billFromAddress,
    billFromGstin:
      typeof o.billFromGstin === "string" ? o.billFromGstin.toUpperCase().slice(0, 32) : d.billFromGstin,
    billToName: typeof o.billToName === "string" ? o.billToName.slice(0, 2000) : d.billToName,
    billToAddress: typeof o.billToAddress === "string" ? o.billToAddress.slice(0, 4000) : d.billToAddress,
    billToGstin: typeof o.billToGstin === "string" ? o.billToGstin.toUpperCase().slice(0, 32) : d.billToGstin,
    paymentTerms:
      typeof o.paymentTerms === "string" && o.paymentTerms.trim()
        ? o.paymentTerms.slice(0, 4000)
        : d.paymentTerms,
    footerNote: typeof o.footerNote === "string" ? o.footerNote.slice(0, 2000) : d.footerNote,
    aggregateQtyDefault: o.aggregateQtyDefault === false ? false : true,
    gstRows: clampGstRows(o.gstRows),
  };
}

function clampSubcontractorBillLines(rows: unknown): SubcontractorBillLineSetting[] {
  if (!Array.isArray(rows)) return [];
  const out: SubcontractorBillLineSetting[] = [];
  for (const r of rows) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    const milestoneTemplateId = typeof o.milestoneTemplateId === "string" ? o.milestoneTemplateId.trim() : "";
    const milestoneTemplateItemId =
      typeof o.milestoneTemplateItemId === "string" ? o.milestoneTemplateItemId.trim() : "";
    const lineLabel = typeof o.lineLabel === "string" ? o.lineLabel.trim().slice(0, 512) : "";
    if (!milestoneTemplateItemId || !lineLabel) continue;
    const isTimeline =
      o.billSource === "timeline" || milestoneTemplateItemId.startsWith("tlbill:");
    if (!isTimeline && !milestoneTemplateId) continue;
    const qtyRaw = Number(o.qty);
    const rateRaw = Number(o.rate);
    out.push({
      billSource: isTimeline ? "timeline" : "milestone",
      milestoneTemplateId,
      milestoneTemplateItemId,
      milestoneTitle: typeof o.milestoneTitle === "string" ? o.milestoneTitle.trim().slice(0, 512) : "",
      lineLabel,
      timelinePhaseIds: Array.isArray(o.timelinePhaseIds)
        ? o.timelinePhaseIds.map((x) => String(x).trim()).filter(Boolean)
        : [],
      timelinePhaseTitles: Array.isArray(o.timelinePhaseTitles)
        ? o.timelinePhaseTitles.map((x) => String(x).trim()).filter(Boolean)
        : [],
      qty: Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1,
      unit: typeof o.unit === "string" && o.unit.trim() ? o.unit.trim().slice(0, 64) : "LS",
      rate: Number.isFinite(rateRaw) && rateRaw >= 0 ? rateRaw : 0,
      hsnCode: typeof o.hsnCode === "string" ? o.hsnCode.toUpperCase().slice(0, 40) : "",
    });
  }
  return out;
}

function migrateLegacyFlatSettings(o: Record<string, unknown>): InvoiceFormatSettings {
  const sharedBlock = clampTypeFormatBlock({
    billFromName: o.billFromName,
    billFromAddress: o.billFromAddress,
    billFromGstin: o.billFromGstin,
    billToName: o.billToName,
    billToAddress: o.billToAddress,
    billToGstin: o.billToGstin,
    paymentTerms: o.paymentTerms,
    footerNote: o.footerNote,
    aggregateQtyDefault: o.aggregateQtyDefault,
    gstRows: o.gstRows,
  });
  const d = defaultInvoiceFormatSettings();
  return {
    version: 2,
    documentTitleMaterial:
      typeof o.documentTitleMaterial === "string" && o.documentTitleMaterial.trim()
        ? o.documentTitleMaterial.trim().slice(0, 120)
        : d.documentTitleMaterial,
    documentTitleSubcontractor:
      typeof o.documentTitleSubcontractor === "string" && o.documentTitleSubcontractor.trim()
        ? o.documentTitleSubcontractor.trim().slice(0, 120)
        : d.documentTitleSubcontractor,
    documentTitleLabour:
      typeof o.documentTitleLabour === "string" && o.documentTitleLabour.trim()
        ? o.documentTitleLabour.trim().slice(0, 120)
        : d.documentTitleLabour,
    letterheadTagline:
      typeof o.letterheadTagline === "string" ? o.letterheadTagline.slice(0, 240) : d.letterheadTagline,
    logoPrimaryUrl: normalizeLetterheadLogoUrl(
      typeof o.logoPrimaryUrl === "string" ? o.logoPrimaryUrl.trim().slice(0, 5000000) : d.logoPrimaryUrl,
    ),
    logoSecondaryUrl: normalizeLetterheadLogoUrl(
      typeof o.logoSecondaryUrl === "string" ? o.logoSecondaryUrl.trim().slice(0, 5000000) : d.logoSecondaryUrl,
    ),
    logoPrimaryHeightPx: Number.isFinite(Number(o.logoPrimaryHeightPx))
      ? clampLogoHeight(Number(o.logoPrimaryHeightPx))
      : d.logoPrimaryHeightPx,
    logoSecondaryHeightPx: Number.isFinite(Number(o.logoSecondaryHeightPx))
      ? clampLogoHeight(Number(o.logoSecondaryHeightPx))
      : d.logoSecondaryHeightPx,
    accentColorHex:
      typeof o.accentColorHex === "string" ? sanitizeAccentHex(o.accentColorHex) : d.accentColorHex,
    material: clampTypeFormatBlock(o.material, sharedBlock),
    subcontractor: clampTypeFormatBlock(o.subcontractor, sharedBlock),
    labour: clampTypeFormatBlock(o.labour, sharedBlock),
    subcontractorBillLines: clampSubcontractorBillLines(o.subcontractorBillLines),
    labourBillLines: clampSubcontractorBillLines(o.labourBillLines),
  };
}

export function normalizeInvoiceFormatSettings(raw: unknown): InvoiceFormatSettings {
  const d = defaultInvoiceFormatSettings();
  if (!raw || typeof raw !== "object") return d;
  const o = raw as Record<string, unknown>;

  if (!o.material && (o.billFromName !== undefined || o.version === 1)) {
    return migrateLegacyFlatSettings(o);
  }

  return {
    version: 2,
    documentTitleMaterial:
      typeof o.documentTitleMaterial === "string" && o.documentTitleMaterial.trim()
        ? o.documentTitleMaterial.trim().slice(0, 120)
        : d.documentTitleMaterial,
    documentTitleSubcontractor:
      typeof o.documentTitleSubcontractor === "string" && o.documentTitleSubcontractor.trim()
        ? o.documentTitleSubcontractor.trim().slice(0, 120)
        : d.documentTitleSubcontractor,
    documentTitleLabour:
      typeof o.documentTitleLabour === "string" && o.documentTitleLabour.trim()
        ? o.documentTitleLabour.trim().slice(0, 120)
        : d.documentTitleLabour,
    letterheadTagline:
      typeof o.letterheadTagline === "string" ? o.letterheadTagline.slice(0, 240) : d.letterheadTagline,
    logoPrimaryUrl: normalizeLetterheadLogoUrl(
      typeof o.logoPrimaryUrl === "string" ? o.logoPrimaryUrl.trim().slice(0, 5000000) : d.logoPrimaryUrl,
    ),
    logoSecondaryUrl: normalizeLetterheadLogoUrl(
      typeof o.logoSecondaryUrl === "string" ? o.logoSecondaryUrl.trim().slice(0, 5000000) : d.logoSecondaryUrl,
    ),
    logoPrimaryHeightPx: Number.isFinite(Number(o.logoPrimaryHeightPx))
      ? clampLogoHeight(Number(o.logoPrimaryHeightPx))
      : d.logoPrimaryHeightPx,
    logoSecondaryHeightPx: Number.isFinite(Number(o.logoSecondaryHeightPx))
      ? clampLogoHeight(Number(o.logoSecondaryHeightPx))
      : d.logoSecondaryHeightPx,
    accentColorHex:
      typeof o.accentColorHex === "string" ? sanitizeAccentHex(o.accentColorHex) : d.accentColorHex,
    material: clampTypeFormatBlock(o.material, d.material),
    subcontractor: clampTypeFormatBlock(o.subcontractor, d.subcontractor),
    labour: clampTypeFormatBlock(o.labour, d.labour),
    subcontractorBillLines: clampSubcontractorBillLines(o.subcontractorBillLines),
    labourBillLines: clampSubcontractorBillLines(o.labourBillLines),
  };
}

export function loadInvoiceFormatSettings(): InvoiceFormatSettings {
  try {
    const raw = localStorage.getItem(INVOICE_FORMAT_STORAGE_KEY);
    if (!raw) return defaultInvoiceFormatSettings();
    return normalizeInvoiceFormatSettings(JSON.parse(raw) as unknown);
  } catch {
    return defaultInvoiceFormatSettings();
  }
}

export function saveInvoiceFormatSettings(s: InvoiceFormatSettings): void {
  localStorage.setItem(INVOICE_FORMAT_STORAGE_KEY, JSON.stringify(normalizeInvoiceFormatSettings(s)));
}

export function formatBlockForInvoiceType(
  s: InvoiceFormatSettings,
  type: InvoiceType,
): InvoiceTypeFormatBlock {
  if (type === "material") return s.material;
  if (type === "subcontractor") return s.subcontractor;
  return s.labour;
}

export function documentTitleForInvoiceType(s: InvoiceFormatSettings, type: InvoiceType): string {
  if (type === "material") return s.documentTitleMaterial;
  if (type === "subcontractor") return s.documentTitleSubcontractor;
  return s.documentTitleLabour;
}

export function formatBillBlock(name: string, address: string, gstin: string): string {
  const parts = [name.trim(), address.trim(), gstin.trim() ? `GSTIN: ${gstin.trim()}` : ""].filter(Boolean);
  return parts.join("\n");
}

export function buildFormatBillFromBlock(invoiceType: InvoiceType, settings?: InvoiceFormatSettings): string {
  const s = settings ?? loadInvoiceFormatSettings();
  const b = formatBlockForInvoiceType(s, invoiceType);
  return formatBillBlock(b.billFromName, b.billFromAddress, b.billFromGstin);
}

export function buildFormatBillToBlock(invoiceType: InvoiceType, settings?: InvoiceFormatSettings): string {
  const s = settings ?? loadInvoiceFormatSettings();
  const b = formatBlockForInvoiceType(s, invoiceType);
  return formatBillBlock(b.billToName, b.billToAddress, b.billToGstin);
}

export function billFromFieldsForPdf(
  header: { billFromText: string; partyFirmName: string; partyAddress: string; partyGstin: string; invoiceType: InvoiceType },
  settings?: InvoiceFormatSettings,
): { name: string; address: string; gstin: string } {
  const s = settings ?? loadInvoiceFormatSettings();
  const b = formatBlockForInvoiceType(s, header.invoiceType);
  const stored = parseBillBlockText(header.billFromText);
  return {
    name: b.billFromName.trim() || stored.name || header.partyFirmName.trim(),
    address: b.billFromAddress.trim() || stored.address || header.partyAddress.trim(),
    gstin: b.billFromGstin.trim() || stored.gstin || header.partyGstin.trim(),
  };
}

export function billToFieldsForPdf(
  header: { billToText: string; invoiceType: InvoiceType },
  settings?: InvoiceFormatSettings,
): { name: string; address: string; gstin: string } {
  const s = settings ?? loadInvoiceFormatSettings();
  const b = formatBlockForInvoiceType(s, header.invoiceType);
  const stored = parseBillBlockText(header.billToText);
  return {
    name: stored.name || b.billToName.trim(),
    address: stored.address || b.billToAddress.trim(),
    gstin: stored.gstin || b.billToGstin.trim(),
  };
}

function parseBillBlockText(text: string): { name: string; address: string; gstin: string } {
  const lines = String(text ?? "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return { name: "", address: "", gstin: "" };
  const gstLine = lines.find((l) => /^GSTIN:/i.test(l));
  const gstin = gstLine ? gstLine.replace(/^GSTIN:\s*/i, "").trim() : "";
  const rest = lines.filter((l) => l !== gstLine);
  return {
    name: rest[0] ?? "",
    address: rest.slice(1).join("\n"),
    gstin,
  };
}

export function gstEditorRowsToSaved(rows: Array<{ label: string; rateInput: string }>): InvoiceFormatGstRowSaved[] {
  return rows.map((r) => {
    const n = Number.parseFloat(String(r.rateInput).replace(/,/g, "").trim());
    return {
      label: (r.label || "Tax").trim().slice(0, 64) || "Tax",
      ratePercent: Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0,
    };
  });
}

export function savedGstToEditorRows(
  rows: InvoiceFormatGstRowSaved[],
): Array<{ id: string; label: string; rateInput: string }> {
  return rows.map((r, i) => ({
    id: `gst-saved-${i}-${r.label.replace(/\W+/g, "").slice(0, 12)}`,
    label: r.label,
    rateInput: String(r.ratePercent),
  }));
}

export function totalGstRatePercent(s: InvoiceFormatSettings, type: InvoiceType): number {
  return activeGstRows(s, type).reduce((sum, g) => sum + g.ratePercent, 0);
}
